import React, { useState, useEffect, useRef } from "react";
import { DndContext, useDraggable } from "@dnd-kit/core"; // Drag and Drop ke liye
import { create } from "zustand";

// --- ICONS (Material-UI se) ---
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import SaveIcon from "@mui/icons-material/Save";
import LayersIcon from "@mui/icons-material/Layers";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteIcon from "@mui/icons-material/Delete"; // Delete icon import kiya gaya

// --- MATERIAL-UI COMPONENTS ---
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Paper,
  TextField,
  Divider,
  Tooltip,
} from "@mui/material";

// --- FIREBASE SETUP (Aapki file se import karein) ---
// NOTE: Yeh file aapko alag se banani hogi 'src/firebase.js' mein
// import { db } from './firebase';
// import { doc, setDoc, getDoc } from "firebase/firestore";

// Mock Firebase functions, inko aap asli Firebase functions se replace karein
const db = {}; // Mock db object
const doc = (db, collection, id) => ({ db, collection, id });
const setDoc = async (docRef, data) => {
  console.log("Saving to Firestore:", JSON.stringify(data, null, 2));
  localStorage.setItem(docRef.id, JSON.stringify(data));
  return Promise.resolve();
};
const getDoc = async (docRef) => {
  console.log("Loading from Firestore:", docRef.id);
  const data = localStorage.getItem(docRef.id);
  if (data) {
    return Promise.resolve({
      exists: () => true,
      data: () => JSON.parse(data),
    });
  }
  return Promise.resolve({ exists: () => false });
};
// End of Mock Firebase

// ====================================================================================
// 1. ZUSTAND STORE: YEH HAMARE EDITOR KA DIMAAG (BRAIN) HAI
// ====================================================================================
const useBlockStore = create((set, get) => ({
  blocks: [], // Saare blocks (text, image, etc.) yahan store honge
  selectedBlockId: null, // Kaunsa block currently selected hai

  addBlock: (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      x: 50,
      y: 50,
      z: get().blocks.length,
      width: type === "text" ? 250 : 200,
      height: type === "text" ? 100 : 150,
      data:
        type === "text"
          ? { text: "Yahan type karein...", fontSize: 16, color: "#000000" }
          : {
              url: "https://placehold.co/600x400/EEE/31343C?text=Image",
              radius: 8,
            },
    };
    set((state) => ({ blocks: [...state.blocks, newBlock] }));
  },

  updateBlock: (id, updates) => {
    set((state) => ({
      blocks: state.blocks.map((block) =>
        block.id === id ? { ...block, ...updates } : block
      ),
    }));
  },

  deleteBlock: (id) => {
    set((state) => ({
      blocks: state.blocks.filter((block) => block.id !== id),
      selectedBlockId:
        state.selectedBlockId === id ? null : state.selectedBlockId,
    }));
  },

  setSelectedBlockId: (id) => set({ selectedBlockId: id }),

  bringToFront: (id) => {
    const blocks = get().blocks;
    const maxZ = Math.max(...blocks.map((b) => b.z), 0);
    get().updateBlock(id, { z: maxZ + 1 });
  },
  sendToBack: (id) => {
    const blocks = get().blocks;
    const minZ = Math.min(...blocks.map((b) => b.z), 0);
    get().updateBlock(id, { z: minZ - 1 });
  },

  loadBlocks: (loadedBlocks) => set({ blocks: loadedBlocks || [] }),
}));

// ====================================================================================
// 2. BLOCK COMPONENT: CUSTOM DRAG & RESIZE LOGIC KE SAATH
// ====================================================================================
const Block = ({ id }) => {
  const block = useBlockStore((state) => state.blocks.find((b) => b.id === id));
  const updateBlock = useBlockStore((state) => state.updateBlock);
  const setSelectedBlockId = useBlockStore((state) => state.setSelectedBlockId);
  const selectedBlockId = useBlockStore((state) => state.selectedBlockId);
  const deleteBlock = useBlockStore((state) => state.deleteBlock);

  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef(null);

  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id });

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  if (!block) return null;

  const isSelected = block.id === selectedBlockId;

  const style = {
    position: "absolute",
    left: block.x,
    top: block.y,
    width: block.width,
    height: block.height,
    zIndex: isSelected ? 1000 : block.z,
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : "none",
    boxSizing: "border-box",
    outline: isSelected ? "2px solid #1976d2" : "none",
    transition: "outline 0.1s ease-in-out",
    cursor: "move",
  };

  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    setSelectedBlockId(block.id);

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = block.width;
    const startHeight = block.height;

    const doResize = (moveEvent) => {
      const newWidth = startWidth + (moveEvent.clientX - startX);
      const newHeight = startHeight + (moveEvent.clientY - startY);
      updateBlock(id, {
        width: newWidth > 50 ? newWidth : 50,
        height: newHeight > 50 ? newHeight : 50,
      });
    };

    const stopResize = () => {
      window.removeEventListener("mousemove", doResize);
      window.removeEventListener("mouseup", stopResize);
    };

    window.addEventListener("mousemove", doResize);
    window.addEventListener("mouseup", stopResize);
  };

  const handleDoubleClick = () => {
    if (block.type === "text") {
      setIsEditing(true);
    }
  };

  const handleTextBlur = () => {
    setIsEditing(false);
  };

  const handleTextChange = (e) => {
    updateBlock(id, { data: { ...block.data, text: e.target.value } });
  };

  const renderContent = () => {
    switch (block.type) {
      case "text":
        if (isEditing) {
          return (
            <textarea
              ref={textareaRef}
              value={block.data.text}
              onChange={handleTextChange}
              onBlur={handleTextBlur}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                padding: "0px",
                margin: "0",
                background: "transparent",
                resize: "none",
                outline: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: `${block.data.fontSize}px`,
                color: block.data.color,
              }}
            />
          );
        }
        return (
          <Typography
            style={{
              fontSize: `${block.data.fontSize}px`,
              color: block.data.color,
              height: "100%",
              width: "100%",
              overflow: "hidden",
              userSelect: "none",
              padding: "0px",
            }}
          >
            {block.data.text}
          </Typography>
        );
      case "image":
        return (
          <img
            src={block.data.url}
            alt="user content"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: `${block.data.radius}px`,
            }}
            draggable="false"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedBlockId(block.id);
      }}
      onDoubleClick={handleDoubleClick}
    >
      {renderContent()}
      {isSelected && (
        <>
          <Box
            onMouseDown={handleResizeMouseDown}
            sx={{
              position: "absolute",
              bottom: "-5px",
              right: "-5px",
              width: "12px",
              height: "12px",
              backgroundColor: "#1976d2",
              border: "2px solid white",
              borderRadius: "50%",
              cursor: "nwse-resize",
              "&:hover": {
                transform: "scale(1.2)",
              },
            }}
          />
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              deleteBlock(id);
            }}
            sx={{
              position: "absolute",
              top: "-14px",
              right: "-14px",
              backgroundColor: "error.main",
              color: "white",
              padding: "2px",
              "&:hover": {
                backgroundColor: "error.dark",
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </Box>
  );
};

// ====================================================================================
// 3. PROPERTIES PANEL: SELECTED BLOCK KI STYLING KE LIYE
// ====================================================================================
const PropertiesPanel = () => {
  const selectedBlock = useBlockStore((state) =>
    state.blocks.find((b) => b.id === state.selectedBlockId)
  );
  const updateBlock = useBlockStore((state) => state.updateBlock);
  const bringToFront = useBlockStore((state) => state.bringToFront);
  const sendToBack = useBlockStore((state) => state.sendToBack);

  if (!selectedBlock) {
    return (
      <Paper
        elevation={3}
        sx={{ p: 2, width: 280, height: "calc(100vh - 64px)" }}
      >
        <Typography variant="h6" gutterBottom>
          Properties
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Koi block select karein uski properties edit karne ke liye.
        </Typography>
      </Paper>
    );
  }

  const handleDataChange = (field, value) => {
    updateBlock(selectedBlock.id, {
      data: { ...selectedBlock.data, [field]: value },
    });
  };

  return (
    <Paper
      elevation={3}
      sx={{ p: 2, width: 280, height: "calc(100vh - 64px)", overflowY: "auto" }}
    >
      <Typography variant="h6" gutterBottom>
        Properties
      </Typography>
      <Typography variant="caption">ID: {selectedBlock.id}</Typography>
      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle2">Layering</Typography>
        <Box>
          <Tooltip title="Bring to Front">
            <IconButton
              size="small"
              onClick={() => bringToFront(selectedBlock.id)}
            >
              <ArrowUpwardIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Send to Back">
            <IconButton
              size="small"
              onClick={() => sendToBack(selectedBlock.id)}
            >
              <ArrowDownwardIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />

      {selectedBlock.type === "text" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Content"
            variant="outlined"
            multiline
            rows={4}
            fullWidth
            value={selectedBlock.data.text}
            onChange={(e) => handleDataChange("text", e.target.value)}
          />
          <TextField
            label="Font Size"
            type="number"
            variant="outlined"
            fullWidth
            value={selectedBlock.data.fontSize}
            onChange={(e) =>
              handleDataChange("fontSize", parseInt(e.target.value) || 16)
            }
          />
          <TextField
            label="Color"
            type="color"
            variant="outlined"
            fullWidth
            value={selectedBlock.data.color}
            onChange={(e) => handleDataChange("color", e.target.value)}
            sx={{ height: 56 }}
          />
        </Box>
      )}

      {selectedBlock.type === "image" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Image URL"
            variant="outlined"
            fullWidth
            value={selectedBlock.data.url}
            onChange={(e) => handleDataChange("url", e.target.value)}
          />
          <TextField
            label="Border Radius (px)"
            type="number"
            variant="outlined"
            fullWidth
            value={selectedBlock.data.radius}
            onChange={(e) =>
              handleDataChange("radius", parseInt(e.target.value) || 0)
            }
          />
        </Box>
      )}
    </Paper>
  );
};

// ====================================================================================
// 4. TOOLBAR: NAYE BLOCKS ADD KARNE AUR SAVE KARNE KE LIYE
// ====================================================================================
const EditorToolbar = ({ onSave }) => {
  const addBlock = useBlockStore((state) => state.addBlock);

  return (
    <AppBar position="static">
      <Toolbar>
        <LayersIcon sx={{ mr: 2 }} />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Infinity Canvas Editor
        </Typography>
        <Tooltip title="Add Text Block">
          <IconButton color="inherit" onClick={() => addBlock("text")}>
            <TextFieldsIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add Image Block">
          <IconButton color="inherit" onClick={() => addBlock("image")}>
            <AddPhotoAlternateIcon />
          </IconButton>
        </Tooltip>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ mx: 1, my: 1, borderColor: "rgba(255,255,255,0.2)" }}
        />
        <Tooltip title="Save Layout">
          <IconButton color="inherit" onClick={onSave}>
            <SaveIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

// ====================================================================================
// 5. CANVAS: YEH HAMARA MAIN WORKSPACE HAI
// ====================================================================================
const Canvas = () => {
  const blocks = useBlockStore((state) => state.blocks);
  const setSelectedBlockId = useBlockStore((state) => state.setSelectedBlockId);

  const handleCanvasClick = (e) => {
    if (e.target === e.currentTarget) {
      setSelectedBlockId(null);
    }
  };

  return (
    <Box
      onClick={handleCanvasClick}
      sx={{
        flexGrow: 1,
        position: "relative",
        backgroundColor: "#f4f6f8",
        backgroundImage: "radial-gradient(#d7d7d7 1px, transparent 1px)",
        backgroundSize: "20px 20px",
        height: "calc(100vh - 64px)",
        overflow: "hidden",
      }}
    >
      {blocks.map((block) => (
        <Block key={block.id} id={block.id} />
      ))}
    </Box>
  );
};

// ====================================================================================
// 6. BLOG MAKER: SAB KUCHH JODNE WALA MAIN COMPONENT
// ====================================================================================
const BlogMaker = () => {
  const blocks = useBlockStore((state) => state.blocks);
  const loadBlocks = useBlockStore((state) => state.loadBlocks);
  const updateBlock = useBlockStore((state) => state.updateBlock);
  const [loading, setLoading] = useState(true);

  const handleSave = async () => {
    try {
      const docRef = doc(db, "layouts", "my-awesome-blog");
      await setDoc(docRef, { blocks });
      alert("Layout saved successfully!");
    } catch (error) {
      console.error("Error saving layout:", error);
      alert("Error saving layout.");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      const docRef = doc(db, "layouts", "my-awesome-blog");
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        loadBlocks(docSnap.data().blocks);
      } else {
        console.log("No such document! Starting fresh.");
      }
      setLoading(false);
    };
    loadData();
  }, [loadBlocks]);

  const handleDragEnd = (event) => {
    const { active, delta } = event;
    const block = useBlockStore
      .getState()
      .blocks.find((b) => b.id === active.id);
    if (block) {
      updateBlock(active.id, { x: block.x + delta.x, y: block.y + delta.y });
    }
  };

  if (loading) {
    return <Typography>Loading Editor...</Typography>;
  }

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
        <EditorToolbar onSave={handleSave} />
        <Box sx={{ display: "flex", flexGrow: 1 }}>
          <Canvas />
          <PropertiesPanel />
        </Box>
      </Box>
    </DndContext>
  );
};

export default BlogMaker;

// ====================================================================================
// AAPKO APNE App.js MEIN BAS YEHI KARNA HAI:
//
// import BlogMaker from './BlogMaker';
// import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
//
// const theme = createTheme();
//
// function App() {
//   return (
//     <ThemeProvider theme={theme}>
//       <CssBaseline />
//       <BlogMaker />
//     </ThemeProvider>
//   );
// }
//
// export default App;
// ====================================================================================
